import { useMutation, useQuery } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { Database, Clock, ChevronDown } from 'lucide-react'
import { SettingsSection } from '~/components/common/SettingsSection'
import { SettingsControlItem, type DropdownOption } from '~/components/common/SettingsControlItem'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '~/components/common/dropdowns/DropdownMenu'
import { AccountType } from '~/constants/settings'
import { useBillingContext } from '~/contexts/BillingContext'
import { useWorkspaceContext } from '~/contexts/WorkspaceContext'
import { QueryKeys, queryClient } from '~/services/queryClient'
import { BetterToast } from '~/utils/toast'
import { isWorkspaceAdmin } from '~/utils/userUtils'

interface RepoCacheTTLResponse {
  ttl_seconds: number
}

interface RepoCacheTTLRequest {
  ttl_seconds: number
}

const ttlPresets: Record<string, number> = {
  '1 hour': 3600,
  '6 hours': 21600,
  '1 day': 86400,
  '3 days': 259200,
  '1 week': 604800,
  '2 weeks': 1209600,
  '1 month': 2592000
}

const ttlDropdownOptions: DropdownOption[] = [
  ...Object.keys(ttlPresets).map(key => ({ key, label: key })),
  { key: 'Custom', label: 'Custom' }
]

const unitOptions: DropdownOption[] = [
  { key: 'hours', label: 'hours' },
  { key: 'days', label: 'days' },
  { key: 'weeks', label: 'weeks' }
]

async function getRepoCacheTTL(workspaceUuid: string): Promise<RepoCacheTTLResponse> {
  const { resolveApiUrl } = await import('~/utils/apiUrl')
  const response = await fetch(
    resolveApiUrl(`/api/workspace/${workspaceUuid}/org/repo-cache-ttl`),
    {
      method: 'GET',
      credentials: 'include'
    }
  )
  if (!response.ok) throw new Error('Failed to fetch repo cache TTL')
  return response.json()
}

async function setRepoCacheTTL(
  workspaceUuid: string,
  request: RepoCacheTTLRequest
): Promise<RepoCacheTTLResponse> {
  const { resolveApiUrl } = await import('~/utils/apiUrl')
  const response = await fetch(
    resolveApiUrl(`/api/workspace/${workspaceUuid}/org/repo-cache-ttl`),
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(request)
    }
  )
  if (!response.ok) {
    const error = await response.text()
    throw new Error(error || 'Failed to set repo cache TTL')
  }
  return response.json()
}

export function RepoCacheTTLSection() {
  const { currentWorkspace } = useWorkspaceContext()
  const workspaceUuid = currentWorkspace?.uuid
  const { permittedCommands } = useBillingContext()
  const [isCustom, setIsCustom] = useState(false)
  const [customValue, setCustomValue] = useState('')
  const [customUnit, setCustomUnit] = useState('days')

  const {
    data: ttlData,
    isLoading,
    error
  } = useQuery({
    queryKey: [QueryKeys.Organizations, 'repo-cache-ttl', workspaceUuid],
    queryFn: () => getRepoCacheTTL(workspaceUuid!),
    retry: 1,
    enabled: !!workspaceUuid
  })

  const { mutate: updateTTL } = useMutation({
    mutationFn: (request: RepoCacheTTLRequest) => setRepoCacheTTL(workspaceUuid!, request),
    onSuccess: () => {
      BetterToast.success('Repository cache settings updated')
      queryClient.invalidateQueries({
        queryKey: [QueryKeys.Organizations, 'repo-cache-ttl', workspaceUuid]
      })
    },
    onError: (err: Error) => {
      BetterToast.error(err.message || 'Failed to update repository cache settings')
    }
  })

  const isEnabled = !!ttlData && ttlData.ttl_seconds > 0

  const currentPresetLabel = (() => {
    if (!ttlData || ttlData.ttl_seconds <= 0) return '1 day'
    const match = Object.entries(ttlPresets).find(([, v]) => v === ttlData.ttl_seconds)
    return match ? match[0] : 'Custom'
  })()

  useEffect(() => {
    if (ttlData && ttlData.ttl_seconds > 0) {
      const match = Object.entries(ttlPresets).find(([, v]) => v === ttlData.ttl_seconds)
      if (!match) {
        setIsCustom(true)
        const seconds = ttlData.ttl_seconds
        if (seconds >= 604800 && seconds % 604800 === 0) {
          setCustomValue((seconds / 604800).toString())
          setCustomUnit('weeks')
        } else if (seconds >= 86400 && seconds % 86400 === 0) {
          setCustomValue((seconds / 86400).toString())
          setCustomUnit('days')
        } else {
          setCustomValue((seconds / 3600).toString())
          setCustomUnit('hours')
        }
      } else {
        setIsCustom(false)
      }
    }
  }, [ttlData])

  const handleToggle = (enabled: boolean) => {
    if (enabled) {
      updateTTL({ ttl_seconds: 86400 })
    } else {
      updateTTL({ ttl_seconds: -1 })
      setIsCustom(false)
    }
  }

  const handlePresetSelect = (key: string) => {
    if (key === 'Custom') {
      setIsCustom(true)
      setCustomValue('')
      setCustomUnit('days')
      return
    }
    setIsCustom(false)
    const seconds = ttlPresets[key]
    if (seconds) {
      updateTTL({ ttl_seconds: seconds })
    }
  }

  const handleUnitSelect = (key: string) => {
    setCustomUnit(key)
    if (customValue) {
      saveCustomValue(customValue, key)
    }
  }

  const saveCustomValue = (value: string, unit: string) => {
    const num = parseFloat(value)
    if (isNaN(num) || num <= 0) return
    const multiplier = unit === 'weeks' ? 604800 : unit === 'days' ? 86400 : 3600
    updateTTL({ ttl_seconds: Math.floor(num * multiplier) })
  }

  const handleCustomBlur = () => {
    if (customValue) {
      saveCustomValue(customValue, customUnit)
    }
  }

  const handleCustomKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && customValue) {
      saveCustomValue(customValue, customUnit)
    }
  }

  const activeLicenseTypes = permittedCommands?.active_license_types || []
  const isPaidAccount =
    activeLicenseTypes.length > 0 && !activeLicenseTypes.every(t => t === AccountType.FLEX)

  if (isLoading || error || !isPaidAccount || !isWorkspaceAdmin(currentWorkspace) || !ttlData) {
    return null
  }

  return (
    <SettingsSection className='gap-3'>
      <div className='flex items-center justify-between h-12'>
        <div className='flex items-center gap-4'>
          <span className='flex items-center justify-center w-6 h-6 shrink-0 text-tokens-neutral-icon-solid'>
            <Database className='w-[18px] h-[18px]' />
          </span>
          <span className='font-sans font-medium text-[14px] leading-[22px] text-[rgba(0,0,0,0.88)] px-2 select-none'>
            Repository Cache Settings
          </span>
        </div>
        <button
          type='button'
          onClick={() => handleToggle(!isEnabled)}
          className='relative w-6 h-[14px] rounded-[16px] cursor-pointer transition-colors'
          style={{ backgroundColor: isEnabled ? 'rgba(0,0,0,0.88)' : '#d9d9d9' }}
          aria-label='Toggle repository cache'
          role='switch'
          aria-checked={isEnabled}
        >
          <span
            className='absolute top-[2px] w-[10px] h-[10px] bg-white rounded-full shadow-[0px_2px_4px_rgba(0,35,11,0.2)] transition-[left]'
            style={{ left: isEnabled ? '12px' : '2px' }}
          />
        </button>
      </div>

      {isEnabled && (
        <>
          <div className='border-t border-tokens-neutral-border-2' />

          {!isCustom ? (
            <SettingsControlItem
              variant='selection'
              icon={<Clock className='w-[18px] h-[18px]' />}
              label='Cache TTL'
              value={currentPresetLabel}
              options={ttlDropdownOptions}
              onSelect={handlePresetSelect}
              hideBorder
            />
          ) : (
            <div className='flex items-center justify-between h-12'>
              <div className='flex items-center gap-4'>
                <span className='flex items-center justify-center w-6 h-6 shrink-0 text-tokens-neutral-icon-solid'>
                  <Clock className='w-[18px] h-[18px]' />
                </span>
                <span className='font-sans font-medium text-[14px] leading-[22px] text-[rgba(0,0,0,0.88)] px-2 select-none'>
                  Cache TTL
                </span>
              </div>
              <div className='flex items-center gap-2'>
                <input
                  type='text'
                  inputMode='decimal'
                  value={customValue}
                  onChange={e => setCustomValue(e.target.value)}
                  onBlur={handleCustomBlur}
                  onKeyDown={handleCustomKeyDown}
                  placeholder='Custom Duration'
                  className='w-[180px] h-10 px-3 bg-white border border-tokens-neutral-border-1 rounded-xl font-sans text-[14px] text-[rgba(0,0,0,0.88)] placeholder:text-[rgba(0,0,0,0.25)] outline-none focus:border-tokens-status-link-1 transition-colors'
                />
                <DropdownMenu>
                  <DropdownMenuTrigger>
                    <button
                      type='button'
                      className='flex items-center gap-1 h-12 py-2 px-3 rounded-xl cursor-pointer hover:opacity-80 transition-opacity'
                    >
                      <span className='font-mono font-semibold text-[15px] leading-normal text-[rgba(0,0,0,0.65)]'>
                        {customUnit}
                      </span>
                      <ChevronDown className='w-[18px] h-[18px] text-tokens-neutral-icon-solid' />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align='end'>
                    {unitOptions.map(opt => (
                      <DropdownMenuItem key={opt.key} onClick={() => handleUnitSelect(opt.key)}>
                        {opt.label}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
          )}
        </>
      )}
    </SettingsSection>
  )
}
